import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, apiFetch, type ApiError } from '../../api/client';
import { Alert, Field } from '../../components/Field';
import { useAuth } from '../../auth/AuthContext';
import { settleUpload } from '../../lib/content-workflow';
import {
  ImportSummary,
  ProposedLessonList,
  toLessonPayload,
  type Draft,
  type PreviewResult,
} from '../../components/ImportFromDocument';

interface Option {
  id: string;
  name: string;
}

interface CourseDetail {
  id: string;
  title: string;
  slug: string;
  status: string;
  shortDescription: string;
  longDescription: string | null;
  thumbnailId: string | null;
  priceMinor: number;
  currency: string;
  isFree: boolean;
  modules: Array<{ id: string; lessons: unknown[] }>;
  publishReadiness: { canPublish: boolean; issues: Array<{ field: string; message: string }> };
}

const STEPS = ['Course', 'Document', 'Lessons', 'Details', 'Finish'] as const;
type Step = 0 | 1 | 2 | 3 | 4;

/**
 * Creating a course, start to finish, in order.
 *
 * The pieces all existed and none of them knew about each other. Creating a
 * course, uploading its document, splitting it into lessons, writing the
 * description, setting a price and adding a cover were six separate screens
 * reached six different ways, and the only thing that told you one had been
 * missed was the publish check refusing at the end with a list of blockers.
 * Over and over in practice: a course was built, submitted, and only then
 * did it say there was no full description and no price.
 *
 * So the order is the interface. Each step does one thing, and Next is
 * refused until that thing is done.
 *
 * One approval, at the end, for the course. The import settles its own
 * content rather than queueing a decision per lesson — the person reviewed
 * every one of them at step three, and asking them to review the same text
 * again in a queue is not a second opinion. The step-five readiness panel
 * still shows the real answer, including the case where content is waiting
 * because the person importing was not entitled to approve it.
 *
 * Nothing here is new capability — every step calls an endpoint the builder
 * already calls. What is new is that the sequence is written down.
 *
 * ## Going back
 *
 * Back is for looking, and for fixing what has not been committed yet. The
 * course is created at the end of step one and the lessons at the end of
 * step three, so those steps stop offering to do it twice once they have;
 * they show what was made and let you move on. Undoing properly is what the
 * builder is for, and the last step links to it.
 */
export function CourseWizard() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [step, setStep] = useState<Step>(0);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  // Step 1 — the course itself.
  const [categories, setCategories] = useState<Option[]>([]);
  const [form, setForm] = useState({ title: '', categoryId: '', shortDescription: '' });
  const [course, setCourse] = useState<CourseDetail | null>(null);

  // Step 2 — the document.
  const [documents, setDocuments] = useState<Array<{ id: string; title: string }>>([]);
  const [documentId, setDocumentId] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const pdfInput = useRef<HTMLInputElement>(null);

  // Step 3 — the lessons it proposes.
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [moduleTitle, setModuleTitle] = useState('');
  const [imported, setImported] = useState<number | null>(null);

  // Step 4 — description, price, cover.
  const [description, setDescription] = useState('');
  const [free, setFree] = useState(false);
  const [price, setPrice] = useState('');
  const coverInput = useRef<HTMLInputElement>(null);
  const [coverName, setCoverName] = useState<string | null>(null);
  const [images, setImages] = useState<Array<{ id: string; title: string }>>([]);

  useEffect(() => {
    api<{ items: Option[] }>('/admin/catalog/categories')
      .then((result) => setCategories(result.items))
      .catch(() => setCategories([]));
    api<{ items: Array<{ id: string; title: string }> }>('/content?kind=DOCUMENT')
      .then((result) => setDocuments(result.items))
      .catch(() => setDocuments([]));
    api<{ items: Array<{ id: string; title: string }> }>('/content?kind=IMAGE')
      .then((result) => setImages(result.items))
      .catch(() => setImages([]));
  }, []);

  const refresh = useCallback(async (id: string) => {
    const detail = await api<CourseDetail>(`/admin/courses/${id}`);
    setCourse(detail);
    return detail;
  }, []);

  /** Upload a file to the content library and return the item it became. */
  async function uploadFile(file: File, title: string) {
    const body = new FormData();
    body.append('file', file);
    body.append('title', title);
    // FormData sets its own multipart boundary, so the JSON default in the
    // fetch wrapper must not be applied here.
    const response = await apiFetch('/content/upload', {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('sx.access') ?? ''}` },
      body,
    });
    const payload = await response.json();
    if (!response.ok) throw payload as ApiError;
    return payload as { id: string; title: string };
  }

  // --------------------------------------------------------------- step one
  async function createCourse() {
    if (course) return true;
    const made = await api<CourseDetail>('/admin/courses', {
      method: 'POST',
      body: JSON.stringify(form),
    });
    await refresh(made.id);
    setModuleTitle(form.title);
    return true;
  }

  // --------------------------------------------------------------- step two
  async function onPdfChosen() {
    const file = pdfInput.current?.files?.[0];
    if (!file || !course) return;
    setBusy(true);
    setError(null);
    try {
      const item = await uploadFile(file, file.name);
      setDocumentId(item.id);
      setDocumentTitle(item.title);
      setDocuments((current) => [item, ...current]);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  // ------------------------------------------------------------- step three
  const runPreview = useCallback(async () => {
    if (!course || !documentId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<PreviewResult>(`/admin/courses/${course.id}/import/preview`, {
        method: 'POST',
        body: JSON.stringify({ contentItemId: documentId }),
      });
      setPreview(result);
      setDrafts(result.lessons.map((lesson) => ({ ...lesson, include: true, open: false })));
      setModuleTitle((current) => result.document.documentTitle ?? current);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }, [course, documentId]);

  // The split is the whole point of this step, so it happens on arrival
  // rather than behind a button nobody asked for.
  useEffect(() => {
    if (step === 2 && preview === null && imported === null) void runPreview();
  }, [step, preview, imported, runPreview]);

  async function commitLessons() {
    if (!course || imported !== null) return true;
    const created = await api<{ lessonsCreated: number }>(`/admin/courses/${course.id}/import`, {
      method: 'POST',
      body: JSON.stringify({
        moduleTitle: moduleTitle.trim() || undefined,
        sourceTitle: documentTitle || undefined,
        lessons: toLessonPayload(drafts),
      }),
    });
    setImported(created.lessonsCreated);
    await refresh(course.id);
    return true;
  }

  // -------------------------------------------------------------- step four
  async function saveDetails() {
    if (!course) return true;
    const amount = Number(price);
    await api(`/admin/courses/${course.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        longDescription: description.trim(),
        isFree: free,
        ...(free ? {} : { priceMinor: Math.round(amount * 100) }),
      }),
    });
    await refresh(course.id);
    return true;
  }

  /** Assign an image that is already in the library. */
  async function pickCover(thumbnailId: string) {
    if (!course || !thumbnailId) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/courses/${course.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ thumbnailId }),
      });
      setCoverName(images.find((image) => image.id === thumbnailId)?.title ?? null);
      await refresh(course.id);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function onCoverChosen() {
    const file = coverInput.current?.files?.[0];
    if (!file || !course) return;
    setBusy(true);
    setError(null);
    try {
      const item = await uploadFile(file, `${course.title} — cover`);
      // An upload starts as a draft, and the course cannot be submitted
      // behind a draft cover, so take it through the content workflow first.
      await settleUpload(item.id, can);
      // Uploading it is only half the job: it still has to become this
      // course's cover, and a half-done upload is worse than none.
      await api(`/admin/courses/${course.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ thumbnailId: item.id }),
      });
      setCoverName(file.name);
      await refresh(course.id);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  // ------------------------------------------------------------- navigation
  const trimmed = description.trim();
  const priceReady = free || (price.trim() !== '' && Number(price) > 0);

  /** Whether Next is allowed to do anything, step by step. */
  const ready: Record<Step, boolean> = {
    0: form.title.trim().length > 0 && form.categoryId !== '' && form.shortDescription.trim() !== '',
    1: documentId !== '',
    2: imported !== null || drafts.some((draft) => draft.include),
    3: trimmed.length >= 40 && priceReady && Boolean(course?.thumbnailId),
    4: true,
  };

  async function next() {
    if (busy || !ready[step]) return;
    setBusy(true);
    setError(null);
    try {
      if (step === 0) await createCourse();
      if (step === 2) await commitLessons();
      if (step === 3) await saveDetails();
      setStep((current) => Math.min(4, current + 1) as Step);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page__head">
        <p style={{ margin: 0, fontSize: 13 }}>
          <Link to="/admin/courses">← All courses</Link>
        </p>
        <h1 className="page__title" style={{ marginTop: 6 }}>
          New course
        </h1>
        <p className="page__lede">
          Five steps, in order. One approval at the end, for the course — not one per lesson.
        </p>
      </div>

      <ol className="wiz__steps">
        {STEPS.map((label, index) => (
          <li
            key={label}
            className={`wiz__step ${index === step ? 'wiz__step--now' : ''} ${
              index < step ? 'wiz__step--done' : ''
            }`}
          >
            <span className="wiz__num">{index < step ? '✓' : index + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}

      <div className="card">
        <div className="card__body">
          {step === 0 ? (
            <>
              <Field
                label="Title"
                required
                maxLength={250}
                value={form.title}
                disabled={Boolean(course)}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
              />
              <div className="field">
                <label className="field__label" htmlFor="wiz-cat">
                  Category
                </label>
                <select
                  id="wiz-cat"
                  className="field__input"
                  value={form.categoryId}
                  disabled={Boolean(course)}
                  onChange={(event) => setForm({ ...form, categoryId: event.target.value })}
                >
                  <option value="">Choose a category…</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <Field
                label="Short description"
                required
                maxLength={500}
                hint="One sentence, shown on the course card."
                value={form.shortDescription}
                disabled={Boolean(course)}
                onChange={(event) => setForm({ ...form, shortDescription: event.target.value })}
              />
              {course ? (
                <Alert kind="info" title={`Created as a draft: ${course.title}`} />
              ) : null}
            </>
          ) : null}

          {step === 1 ? (
            <>
              <p className="muted" style={{ marginTop: 0 }}>
                Upload a PDF or Word (.docx) document. One lesson comes out of each top-level
                section, with its headings, bullet lists, tables, code and links kept, and each
                lesson opens with its own contents. A Word file keeps the most exact structure.
              </p>
              <label className="field__label" htmlFor="wiz-pdf">
                Upload a PDF or Word document
              </label>
              <input
                id="wiz-pdf"
                ref={pdfInput}
                className="field__input"
                type="file"
                accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
                disabled={busy}
                onChange={() => void onPdfChosen()}
              />

              <div className="field" style={{ marginTop: 16 }}>
                <label className="field__label" htmlFor="wiz-doc">
                  Or choose one already in the library
                </label>
                <select
                  id="wiz-doc"
                  className="field__input"
                  value={documentId}
                  onChange={(event) => {
                    setDocumentId(event.target.value);
                    setDocumentTitle(
                      documents.find((d) => d.id === event.target.value)?.title ?? '',
                    );
                  }}
                >
                  <option value="">Nothing chosen yet</option>
                  {documents.map((document) => (
                    <option key={document.id} value={document.id}>
                      {document.title}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          {step === 2 ? (
            <>
              {imported !== null ? (
                <Alert
                  kind="success"
                  title={`${imported} lesson${imported === 1 ? '' : 's'} created, with their content ready. The only approval left is the course's own.`}
                />
              ) : (
                <div className="field">
                  <label className="field__label" htmlFor="wiz-module">
                    Put them in a module called
                  </label>
                  <input
                    id="wiz-module"
                    className="field__input"
                    value={moduleTitle}
                    maxLength={250}
                    onChange={(event) => setModuleTitle(event.target.value)}
                  />
                </div>
              )}
            </>
          ) : null}

          {step === 3 ? (
            <>
              <div className="field">
                <label className="field__label" htmlFor="wiz-desc">
                  Full description
                </label>
                <textarea
                  id="wiz-desc"
                  className="field__input"
                  rows={5}
                  maxLength={20000}
                  value={description}
                  placeholder="What the course covers, who it is for, and what a learner can do by the end."
                  onChange={(event) => setDescription(event.target.value)}
                />
                <p className="field__hint">
                  {trimmed.length < 40
                    ? `At least 40 characters — ${40 - trimmed.length} to go.`
                    : `${trimmed.length} characters.`}
                </p>
              </div>

              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={free}
                  onChange={(event) => setFree(event.target.checked)}
                />
                This course is free
              </label>

              {free ? null : (
                <Field
                  label={`Price (${course?.currency ?? 'INR'})`}
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={price}
                  invalid={!priceReady}
                  hint="The amount a learner pays, not the minor units."
                  onChange={(event) => setPrice(event.target.value)}
                />
              )}

              <div className="field" style={{ marginTop: 16 }}>
                <label className="field__label" htmlFor="wiz-cover">
                  Cover image
                </label>
                <input
                  id="wiz-cover"
                  ref={coverInput}
                  className="field__input"
                  type="file"
                  accept="image/*"
                  disabled={busy}
                  onChange={() => void onCoverChosen()}
                />
                <p className="field__hint">
                  {course?.thumbnailId
                    ? `Cover set${coverName ? `: ${coverName}` : ''}.`
                    : 'A course cannot be published without one.'}
                </p>
              </div>

              {/* Both routes, because both are real: uploading is what
                  happens the first time, and picking an existing image is
                  what happens when a series shares artwork. Offering only
                  the upload is how a library fills with duplicates. */}
              <div className="field">
                <label className="field__label" htmlFor="wiz-cover-pick">
                  Or choose one already in the library
                </label>
                <select
                  id="wiz-cover-pick"
                  className="field__input"
                  value={course?.thumbnailId ?? ''}
                  disabled={busy}
                  onChange={(event) => void pickCover(event.target.value)}
                >
                  <option value="">Nothing chosen yet</option>
                  {images.map((image) => (
                    <option key={image.id} value={image.id}>
                      {image.title}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          {step === 4 && course ? (
            <>
              {course.publishReadiness.canPublish ? (
                <p className="readiness__state readiness__state--ok">✓ Ready to publish</p>
              ) : awaitingApproval(course) ? (
                // Every remaining blocker is content sitting in the review
                // queue. Listing eighteen of them reads as eighteen things
                // gone wrong, when it is one thing with eighteen rows.
                <>
                  <p className="readiness__state readiness__state--blocked">
                    Waiting on approval — {course.publishReadiness.issues.length} lesson
                    {course.publishReadiness.issues.length === 1 ? '' : 's'}
                  </p>
                  <p className="muted" style={{ fontSize: 13 }}>
                    The course itself is complete. Its content is in the review queue and has to be
                    approved and published there before learners can open it.
                  </p>
                  <Link className="btn btn--sm" to="/admin/review">
                    Go to the review queue
                  </Link>
                </>
              ) : (
                <>
                  <p className="readiness__state readiness__state--blocked">
                    ✕ {course.publishReadiness.issues.length} blocker
                    {course.publishReadiness.issues.length === 1 ? '' : 's'}
                  </p>
                  <ul className="readiness__issues">
                    {course.publishReadiness.issues.map((issue) => (
                      <li key={issue.field + issue.message}>{issue.message}</li>
                    ))}
                  </ul>
                </>
              )}

              <div className="row" style={{ marginTop: 16 }}>
                <button
                  className="btn btn--sm"
                  type="button"
                  disabled={busy || !course.publishReadiness.canPublish}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await api(`/admin/courses/${course.id}/submit`, { method: 'POST', body: '{}' });
                      navigate(`/admin/courses/${course.id}`, {
                        state: { notice: 'Submitted for review.' },
                      });
                    } catch (caught) {
                      setError(caught as ApiError);
                      setBusy(false);
                    }
                  }}
                >
                  Submit for review
                </button>
                <Link className="btn btn--ghost btn--sm" to={`/admin/courses/${course.id}`}>
                  Open the full builder
                </Link>
              </div>

            </>
          ) : null}
        </div>

        {/* The proposed lessons sit outside card__body so the list can run
            edge to edge, as it does in the builder's import panel. */}
        {step === 2 && preview && imported === null ? (
          <>
            <ImportSummary result={preview} />
            <ProposedLessonList
              drafts={drafts}
              onUpdate={(number, patch) =>
                setDrafts((current) =>
                  current.map((draft) =>
                    draft.number === number ? { ...draft, ...patch } : draft,
                  ),
                )
              }
            />
          </>
        ) : null}

        {step === 2 && !preview && imported === null ? (
          <div className="spinner">Reading the document…</div>
        ) : null}

        <div className="wiz__nav">
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            disabled={step === 0 || busy}
            onClick={() => setStep((current) => Math.max(0, current - 1) as Step)}
          >
            Back
          </button>
          {step < 4 ? (
            <button
              className="btn btn--sm"
              type="button"
              disabled={busy || !ready[step]}
              onClick={() => void next()}
            >
              {busy ? 'Working…' : nextLabel(step, imported)}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * Is everything still blocking it just content waiting to be approved?
 *
 * Told apart from a real problem by the shape of the message, which the
 * publish check writes and this only reads. Crude, but the alternative is a
 * machine-readable reason code on every issue, and the check already names
 * the field it failed on.
 */
function awaitingApproval(course: CourseDetail): boolean {
  const issues = course.publishReadiness.issues;
  return (
    issues.length > 0 && issues.every((issue) => /is \w+, not published\.$/.test(issue.message))
  );
}

/** Next should say what it is about to do, not only that it moves on. */
function nextLabel(step: Step, imported: number | null): string {
  if (step === 0) return 'Create and continue';
  if (step === 2) return imported === null ? 'Create the lessons' : 'Next';
  if (step === 3) return 'Save and continue';
  return 'Next';
}
